/** Local wall-clock times of a venue (U85): parsing and conversion to UTC for calendars. */

/** Default venue time zone of a board without `meta.timezone`. */
export const DEFAULT_TIMEZONE = 'Europe/Berlin';

const pad = (n: number) => String(n).padStart(2, '0');

/** "14:00", "14.00", "9:30", "14 Uhr", "14h", "1400", "930" (OIL-208) → "14:00"; else null. */
export function parseClock(text: string | undefined): string | null {
  const compact = /^\s*(\d{1,2})(\d{2})\s*(?:uhr|h)?\s*$/i.exec(text ?? '');
  if (compact) return clock(Number(compact[1]), Number(compact[2]));
  const m = /^\s*(\d{1,2})(?:[:.h](\d{2}))?\s*(?:uhr|h)?\s*$/i.exec(text ?? '');
  if (!m) return null;
  const h = Number(m[1]);
  const min = m[2] === undefined ? 0 : Number(m[2]);
  // a bare number is only a time with "Uhr" / "h"
  if (m[2] === undefined && !/[a-z]/i.test(text!)) return null;
  return clock(h, min);
}

function clock(h: number, min: number): string | null {
  return h > 23 || min > 59 ? null : `${pad(h)}:${pad(min)}`;
}

/** "2026-10-12", "12.10.2026", "12.10.26" → "2026-10-12"; anything else → null. */
export function parseDay(text: string | undefined): string | null {
  const s = (text ?? '').trim();
  let y: number, mo: number, d: number;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/.exec(s);
  if (iso) [y, mo, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (de) [d, mo, y] = [Number(de[1]), Number(de[2]), Number(de[3])];
  else return null;
  if (y < 100) y += 2000;
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${pad(mo)}-${pad(d)}`;
}

/** Whether `tz` is an IANA time zone the runtime knows. */
export function isTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Offset of `tz` from UTC at the instant `utcMs`, in minutes. */
function offsetMinutes(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).formatToParts(new Date(utcMs));
  const n = (type: string) => Number(parts.find((p) => p.type === type)!.value);
  const local = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'));
  return Math.round((local - utcMs) / 60_000);
}

/** The instant of a local day + "HH:MM" in `tz` (gaps of DST changes shift forward). */
export function zonedToUtc(day: string, clock: string, tz: string): Date {
  const [y, mo, d] = day.split('-').map(Number) as [number, number, number];
  const [h, min] = clock.split(':').map(Number) as [number, number];
  const naive = Date.UTC(y, mo - 1, d, h, min);
  // two rounds settle the offset around DST changes
  let utc = naive - offsetMinutes(naive, tz) * 60_000;
  utc = naive - offsetMinutes(utc, tz) * 60_000;
  return new Date(utc);
}

/** "HH:MM" + minutes, with the day carried over ("23:30" + 60 → next day "00:30"). */
export function addMinutes(day: string, clock: string, minutes: number): [string, string] {
  const [y, mo, d] = day.split('-').map(Number) as [number, number, number];
  const [h, min] = clock.split(':').map(Number) as [number, number];
  const t = new Date(Date.UTC(y, mo - 1, d, h, min + minutes));
  return [
    `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`,
    `${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}`,
  ];
}
